import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Translate from 'components/Translate';
import Box from 'components/UI/Box/Box';
import PropTypes from 'prop-types';
import { getCMSDocumentSrc } from 'utils/getCMSDocumentSrc';
import CmsLink from 'components/UI/CmsLink';
import Button from 'components/UI/Button';
import { USER_LEVEL } from 'constants/global';
import isGestopagoCard from 'utils/isGestopagoCard';

const IMG_SIZES = {
  xs: 'max-w-[50px]',
  sm: 'max-w-[100px]',
  md: 'max-w-[150px]',
  lg: 'max-w-[200px]',
  xl: 'max-w-[240px]',
  xxl: 'max-w-[280px]',
};

const IMG_EFFECTS = {
  rotate: '-scale-x-100',
};

// Negative margins must mirror the Box PADDINGS scale so images can bleed
// to the card edge when the CMS sends imgMargin='false'.
const BLEEDS = {
  xs: {
    left: '-ml-2.5', right: '-mr-2.5', top: '-mt-2.5', bottom: '-mb-2.5',
  },
  sm: {
    left: '-ml-4', right: '-mr-4', top: '-mt-4', bottom: '-mb-4',
  },
  md: {
    left: '-ml-6', right: '-mr-6', top: '-mt-6', bottom: '-mb-6',
  },
  lg: {
    left: '-ml-8', right: '-mr-8', top: '-mt-8', bottom: '-mb-8',
  },
};

const ALIGNMENTS = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
};

const Card = (props) => {
  const {
    className = '',
    title = '',
    description = '',
    image = {},
    imgPosition = '',
    imgAlignment = '',
    imgEffect = '',
    imgMargin = '',
    imgWidth = '',
    style: auxStyle = '',
    link = {},
    spacing = 'sm',
    onClick = null,
  } = props;

  const { user } = useSelector((state) => state.session);
  const isComplete = user?.level === USER_LEVEL.COMPLETE;
  const isGestopago = isGestopagoCard(link);

  const style = auxStyle || '';
  const cardImg = image?.hash ? getCMSDocumentSrc(image) : '';
  const imgClasses = useMemo(() => {
    const bleed = BLEEDS[spacing] ?? BLEEDS.sm;
    const aux = ['h-auto max-h-full', IMG_SIZES[imgWidth] || IMG_SIZES.md];
    if (imgMargin === 'false') {
      aux.push(imgPosition === 'left' ? bleed.left : bleed.right);
      if (imgAlignment !== 'center') {
        aux.push(imgAlignment === 'start' ? bleed.top : bleed.bottom);
      }
    }
    if (imgEffect && IMG_EFFECTS[imgEffect]) {
      aux.push(IMG_EFFECTS[imgEffect]);
    }
    return aux.join(' ');
  }, [imgMargin, imgPosition, imgAlignment, imgEffect, imgWidth, spacing]);

  if (isGestopago && !isComplete) {
    return null;
  }

  const renderContent = () => (
    <>
      {['2', '3', '4'].includes(style) && title && (
        <Translate className='text-neutral-800' component='h4' literal={title} />
      )}
      {['3', '4'].includes(style) && description && (
        <Translate className='mb-2 text-neutral-500' literal={description} />
      )}
      <div className={`flex justify-between ${ALIGNMENTS[imgAlignment] || ALIGNMENTS.center} gap-4`}>
        <div className={`flex flex-col justify-between ${imgPosition === 'left' ? 'order-1 text-right' : 'order-none'}`}>
          {!['2', '3', '4'].includes(style) && title && (
            <Translate className='text-neutral-800' component='h4' literal={title} />
          )}
          {!['3', '4'].includes(style) && description && (
            <Translate className='mb-1 text-neutral-500' literal={description} />
          )}
          {!['4'].includes(style) && link?.archor && (
            <p className='a mb-0 mt-2 font-bold'>
              {link?.type === 'button' ? <Button text={link?.archor} size='xs' /> : link?.archor}
            </p>
          )}
        </div>
        <div className={imgPosition === 'left' ? 'order-none' : 'order-1'}>
          {cardImg && (
            <img src={cardImg} alt='card' className={imgClasses} />
          )}
        </div>
      </div>
      {['4'].includes(style) && link?.archor && (
        <p className='a mb-0 font-bold'>
          {link?.type === 'button' ? <Button text={link?.archor} size='xs' /> : link?.archor}
        </p>
      )}
    </>
  );

  return (
    <Box className={`mb-4 ${className}`} padding={spacing || 'sm'} onClick={onClick}>
      {link?.link ? (
        <CmsLink to={link.link} external={link.external} className='no-underline'>
          {renderContent()}
        </CmsLink>
      ) : (
        renderContent()
      )}
    </Box>
  );
};

Card.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.shape({
    hash: PropTypes.string,
  }),
  imgPosition: PropTypes.string,
  imgAlignment: PropTypes.string,
  imgMargin: PropTypes.string,
  imgEffect: PropTypes.string,
  imgWidth: PropTypes.oneOf(['', 'xs', 'sm', 'md', 'lg', 'xl']),
  link: PropTypes.shape({
    archor: PropTypes.string,
    link: PropTypes.string,
    external: PropTypes.bool,
    type: PropTypes.string,
  }),
  style: PropTypes.oneOf(['1', '2', '3', '4']),
  spacing: PropTypes.oneOf(['sm', 'md', 'lg']),
  onClick: PropTypes.func,
};

export default Card;
